import { Link } from "@tanstack/react-router";
import { Home, MessageCircle, Settings } from "lucide-react";

import { useGetCounts } from "@/api/inbox";
import { useLanguage } from "@/contexts/language";
import { Num } from "@/features/chat/lib";
import { hapticSelection } from "@/lib/haptics";
import { cn } from "@/lib/utils";

// Bottom navigation (phones only — see SideNav for desktop): three equal
// items, Material-3 style — the active item's icon sits in a soft brand pill
// and its label turns brand, matching the desktop rail's active treatment.

function NavItem({
  to,
  label,
  icon,
  badge,
  exact,
}: {
  to: "/home" | "/chats" | "/settings";
  label: string;
  icon: React.ReactNode;
  badge?: number;
  exact?: boolean;
}) {
  return (
    <Link
      to={to}
      // "/" would match every route under fuzzy matching, so Home opts out.
      activeOptions={exact ? { exact: true } : undefined}
      onClick={hapticSelection}
      className="tw:group tw:flex tw:h-full tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:text-muted-foreground tw:no-underline tw:transition-colors tw:active:scale-95"
      activeProps={{ className: "tw:!text-brand tw:font-semibold" }}
    >
      {/* The pill only shows behind the ACTIVE item's icon — TanStack Link
          stamps data-status="active" on the anchor. */}
      <span className="tw:relative tw:flex tw:h-7 tw:w-14 tw:items-center tw:justify-center tw:rounded-full tw:transition-colors tw:group-data-[status=active]:bg-brand-50">
        {icon}
        {badge != null && badge > 0 && (
          <span className="tw:absolute tw:-top-1 tw:end-1 tw:flex tw:h-4.5 tw:min-w-4.5 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand tw:px-1 tw:text-[10px] tw:font-semibold tw:text-brand-foreground">
            <Num>{badge > 99 ? "99+" : badge}</Num>
          </span>
        )}
      </span>
      <span className="tw:text-[11px] tw:leading-none">{label}</span>
    </Link>
  );
}

export function BottomNav() {
  const { t } = useLanguage();
  const { data: counts } = useGetCounts();

  return (
    <nav
      className={cn(
        "tw:fixed tw:inset-x-0 tw:bottom-0 tw:z-50 tw:border-t tw:border-border tw:bg-background/95 tw:backdrop-blur",
        // Replaced by the vertical SideNav rail from md up.
        "tw:md:hidden",
        "tw:pb-[env(safe-area-inset-bottom)]",
      )}
    >
      <div className="tw:mx-auto tw:flex tw:h-16 tw:max-w-xl tw:items-stretch">
        <NavItem
          to="/chats"
          label={t("Mandoob:Nav.Chats")}
          icon={<MessageCircle className="tw:size-6" strokeWidth={1.75} />}
          badge={counts?.unread}
        />
        <NavItem
          to="/home"
          exact
          label={t("Mandoob:Nav.Home")}
          icon={<Home className="tw:size-6" strokeWidth={1.75} />}
        />
        <NavItem
          to="/settings"
          label={t("Mandoob:Nav.Settings")}
          icon={<Settings className="tw:size-6" strokeWidth={1.75} />}
        />
      </div>
    </nav>
  );
}
